import { call } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Field, Notice } from '@rental/ui'
import { type RefObject, useState } from 'react'
import { api } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OWNER_CODE, OwnerCodeField } from '../components/OwnerCodeField'
import { ProblemNotice, StaffInput } from '../components/ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'

/** A reason of at least this many letters: the loader's rule for a load, the fines' for a record. */
export const REASON_MIN = 3

/**
 * OPS-1: puts a version in force after a question that names it (D11, the
 * change every new contract follows), with a reason for the audit row and the
 * Owner's authenticator code, which the server spends in the same transaction
 * as the switch: a wrong or used code changes nothing, and the question stays
 * open for the next code.
 */
export function ActivateDialog({
  dialogRef,
  version,
  draft,
  onDone,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  version: string | null
  draft: boolean
  onDone: (version: string) => void
}) {
  const { messages: m } = useLocale()
  const copy = m.terms
  const { send, busy, problem, clear } = useSend()
  const [reason, setReason] = useState('')
  const [code, setCode] = useState('')
  const ready = reason.trim().length >= REASON_MIN && OWNER_CODE.test(code)

  async function confirm() {
    if (!version) return
    const done = await send(() =>
      call(api.POST('/v1/staff/terms/current', { body: { version, reason: reason.trim(), code } })),
    )
    // A code is spent whether or not the server accepted the change: a new one is typed either way.
    setCode('')
    if (!done) return
    dialogRef.current?.close()
    setReason('')
    onDone(done.version)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={format(copy.activateTitle, { version: version ?? '' })}
      confirmLabel={copy.confirm}
      cancelLabel={copy.cancel}
      busy={busy}
      ready={ready}
      reasons={[format(copy.why, { min: REASON_MIN })]}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        setCode('')
      }}
    >
      <p>{copy.activateBody}</p>
      {draft ? (
        <Notice tone="warning">
          <p>{copy.activateDraft}</p>
        </Notice>
      ) : null}
      <Field label={copy.reason} hint={copy.reasonHint} required>
        {(control) => (
          <StaffInput
            {...control}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
      <OwnerCodeField label={copy.code} value={code} onChange={setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}
